import { useQuery } from '@apollo/client/react';
import { AccessInfo, TeamType } from 'domain/shared/newProject';
import { GET_STAFFING_DASHBOARD_DATA } from 'domain/staffing/dashboard/dashboard/graphql/staffingQueries';
import { StaffingNoteItem, StaffingRisk } from 'domain/shared/staffing';

export type StaffingProjectResponseItem = {
  id: string;
  legacyId?: number;
  name?: string;
  alias?: string;
  projectManager?: string;
  projectComplexity?: string;
  projectImportance?: string;
  description?: string;
  budget?: { id?: string; startDate?: string; endDate?: string; billingType?: string; fte?: string };
  isArchived?: boolean;
  accountData?: { accountName?: string; accountId?: string; ownerAlias?: string };
  team?: TeamType['team'];
  staffingRisk?: StaffingRisk;
  criticalPeriods?: TeamType['criticalPeriods'];
  upcomingChanges?: TeamType['upcomingChanges'];
  staffingNote?: StaffingNoteItem[];
  accessInfo?: AccessInfo;
};

const buildSearchStringByProjectId = (projectId: string): string => {
  return JSON.stringify({
    filter: {
      id: { eq: projectId },
    },
  });
};

function mapToTeamType(item: StaffingProjectResponseItem): TeamType {
  return {
    id: item.id,
    budget: {
      fte: item.budget?.fte ?? '',
    },
    team:
      item.team ??
      ({
        id: '',
        expectedStructure: [],
        members: [],
        updatedAt: null,
      } as TeamType['team']),
    criticalPeriods: item.criticalPeriods ?? [],
    upcomingChanges: item.upcomingChanges ?? [],
  };
}

export const useStaffingProjectData = (projectId: string | undefined) => {
  const searchString = projectId ? buildSearchStringByProjectId(projectId) : '';

  const { data, loading, error, refetch } = useQuery<{ baseProjects: StaffingProjectResponseItem[] }>(
    GET_STAFFING_DASHBOARD_DATA,
    {
      variables: { searchString },
      skip: !projectId,
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );

  const rawItem = data?.baseProjects?.[0] ?? null;
  const project = rawItem;
  const teamData = rawItem ? mapToTeamType(rawItem) : null;

  const refetchData = async () => {
    const result = await refetch();
    return result?.data?.baseProjects?.[0];
  };

  return {
    project,
    teamData,
    rawItem,
    loading,
    error,
    refetch: refetchData,
    isDataReloading: loading,
  };
};
